import { motion } from "framer-motion";
import { Check } from "lucide-react";

const tiers = [
  {
    name: "Starter",
    setup: 299,
    monthly: 79,
    firstYear: "$1,247",
    tagline: "Get online with presence that punches above its weight.",
    features: ["Up to 3 pages", "Mobile responsive", "Services & contact links", "Basic ongoing maintenance"],
    featured: false,
  },
  {
    name: "Growth",
    setup: 499,
    monthly: 149,
    firstYear: "$2,287",
    tagline: "The full lead-generation machine for a growing crew.",
    features: ["Up to 5 pages", "Project gallery", "Quote inquiry form design", "Local SEO basics", "Monthly content edits"],
    featured: true,
  },
  {
    name: "Premium",
    setup: 899,
    monthly: 249,
    firstYear: "$3,887",
    tagline: "Market-leader presence for established operations.",
    features: ["Up to 10 pages", "Expanded project portfolio", "Detailed quote intake", "Service area & FAQ sections", "Ongoing SEO & content support"],
    featured: false,
  },
];

export default function Pricing() {
  const go = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="pricing" data-testid="agency-pricing-section" className="py-28 lg:py-36 bg-surface/30 border-y gold-border-subtle">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">Transparent Packages</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F8F6F0] tracking-tight max-w-2xl">
            Choose your <span className="gold-text italic">starting point.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              data-testid={`pricing-tier-${t.name.toLowerCase()}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={`relative rounded-3xl p-9 flex flex-col ${
                t.featured
                  ? "trace-border border gold-border-subtle bg-elevated lg:-my-4 lg:py-13"
                  : "border border-zinc-800 bg-surface/60 hover:border-gold/30 transition-colors duration-500"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3.5 left-9 px-4 py-1.5 rounded-full bg-gold text-obsidian font-mono text-[10px] uppercase tracking-[0.2em] font-medium" data-testid="pricing-featured-badge">
                  Most Chosen
                </span>
              )}
              <h3 className="font-serif text-3xl text-[#F8F6F0]">{t.name}</h3>
              <p className="text-sm text-zinc-500 mt-2 leading-relaxed min-h-[40px]">{t.tagline}</p>
              <div className="mt-7 flex items-end gap-2">
                <span className={`font-serif text-5xl ${t.featured ? "gold-text" : "text-[#F8F6F0]"}`} data-testid={`pricing-setup-${t.name.toLowerCase()}`}>${t.setup}</span>
                <span className="text-zinc-500 text-sm pb-1.5">setup</span>
              </div>
              <p className="mt-1.5 font-mono text-xs text-zinc-400 tracking-wide">+ ${t.monthly}/month · First year {t.firstYear}</p>
              <div className="my-7 h-px bg-gradient-to-r from-gold/25 to-transparent" />
              <ul className="space-y-3.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                onClick={go}
                className={`mt-9 w-full py-3.5 rounded-full font-sans font-semibold text-sm tracking-wide transition-all duration-300 ${
                  t.featured
                    ? "bg-gold text-obsidian hover:bg-gold-hover hover:shadow-[0_0_36px_rgba(212,175,55,0.35)]"
                    : "border border-zinc-700 text-zinc-200 hover:border-gold/60 hover:text-gold"
                }`}
              >
                Start with {t.name}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
          Setup plus monthly service. Annual cost varies by package. Every package is customized to your trade.
        </p>
      </div>
    </section>
  );
}
